import * as React from 'react'
import cn from 'classnames'
import styles from './InputStepper.module.css'
import { Icon } from '../icon'
import { Typography } from '../typography'
import type { InputStepperProps } from './InputStepper.types'

const INITIAL_VALUE = 0
const STEP_VALUE = 1

const ICON_SIZE: Record<'S' | 'M', number> = {
  S: 16,
  M: 24,
}

function InputStepper({
  initialValue = INITIAL_VALUE,
  minimumValue = 0,
  maximumValue = Number.MAX_SAFE_INTEGER,
  stepValue = STEP_VALUE,
  decrementButtonAriaLabel = 'decrement',
  incrementButtonAriaLabel = 'increment',
  valueAriaLabel = 'value',
  size = 'M',
  disabled = false,
  onChange = val => val,
  disableInput = false,
  id = '',
  className,
}: InputStepperProps) {
  const [value, setValue] = React.useState(initialValue)

  const canBeDecreased = value - stepValue >= minimumValue
  const canBeIncreased = value + stepValue <= maximumValue

  const handleChangeValue = (itemValue: number) => {
    setValue(itemValue)
    onChange(itemValue)
  }

  const handleValidateValue = (itemValue: number) => {
    if (itemValue < minimumValue) {
      setValue(minimumValue)
      onChange(minimumValue)
      return
    }

    if (itemValue > maximumValue) {
      setValue(maximumValue)
      onChange(maximumValue)
      return
    }

    handleChangeValue(itemValue)
  }

  const decreaseValueHandler = () => {
    if (!canBeDecreased)
      return
    const newValue = value - stepValue
    handleChangeValue(newValue)
  }

  const increaseValueHandler = () => {
    if (!canBeIncreased)
      return
    const newValue = value + stepValue
    handleChangeValue(newValue)
  }

  const wrapperClassName = cn(
    styles.wrapper,
    size === 'S' ? styles.sizeS : styles.sizeM,
    { [styles.wrapperDisabled]: disabled },
    className,
  )

  const buttonDecrementClassName = cn(styles.button, {
    [styles.buttonDisabled]: disabled || !canBeDecreased,
  })

  const buttonIncrementClassName = cn(styles.button, {
    [styles.buttonDisabled]: disabled || !canBeIncreased,
  })

  return (
    <div className={wrapperClassName}>
      <button
        type="button"
        className={buttonDecrementClassName}
        onClick={decreaseValueHandler}
        aria-disabled={disabled || !canBeDecreased}
        aria-label={decrementButtonAriaLabel}
        disabled={disabled || !canBeDecreased}
        data-qa="number-stepper-decrement"
      >
        <Icon glyph="Minus" size={ICON_SIZE[size]} className={styles.icon} aria-hidden="true" />
      </button>
      {!disableInput
        ? (
            <input
              id={id}
              type="number"
              className={styles.value}
              aria-label={valueAriaLabel}
              value={value}
              disabled={disabled}
              onChange={e => handleChangeValue(Number(e.target.value))}
              onBlur={e => handleValidateValue(Number(e.target.value))}
              min={minimumValue}
              max={maximumValue}
            />
          )
        : (
            <Typography variant="body-m-regular-tight">{value}</Typography>
          )}
      <button
        type="button"
        className={buttonIncrementClassName}
        onClick={increaseValueHandler}
        aria-disabled={disabled || !canBeIncreased}
        aria-label={incrementButtonAriaLabel}
        disabled={disabled || !canBeIncreased}
        data-qa="number-stepper-increment"
      >
        <Icon glyph="Plus" size={ICON_SIZE[size]} className={styles.icon} aria-hidden="true" />
      </button>
    </div>
  )
}

InputStepper.displayName = 'InputStepper'

export default InputStepper
